import React, { useEffect, useState } from 'react';
import { Form, reduxForm, getFormValues } from 'redux-form';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { Col, Row } from 'react-bootstrap';

import { actions as channelActions } from '../../../channel/redux/channelActions';
import { actions as brokerActions } from '../../../broker/redux/brokerActions';
import { Box, Button } from '../../../../common/components';
import { EmailContent, LetterContent } from '../../../whitelabel/components';
import { InternalButtonBar } from '../../../layout/components';

import getIncludedResource from '../../../../helpers/getIncludedResource';

const OWNER_WHITELABEL = 'jtc-whitelabel-b2c';
const OwnerContent = ({
  formValues,
  change,
  handleSubmit,
  actions,
  channelActions,
  submitting,
}) => {
  const [renewalTemplates, setRenewalTemplates] = useState(null);
  const [policyTemplates, setPolicyTemplates] = useState(null);
  const [renewalLetterTemplates, setRenewalLetterTemplates] = useState(null);

  useEffect(() => {
    handleSetUpOwnerContent();
  }, []);

  const handleSetUpOwnerContent = () => {
    Promise.resolve(
      channelActions.getChannelByKey(OWNER_WHITELABEL, [
        'theme',
        'contents',
        'contents.template',
      ]),
    ).then((channel) => {
      const contents = getIncludedResource(channel.data, channel.included, 'contents');
      getEmailTemplates(contents, channel);
      getLetterTemplates(contents, channel);
    });
  };

  const getContents = (templates, contents, resourceName) => {
    return templates.map((template) => {
      const matchedContent = contents
        ? contents.find(
            (content) =>
              content.relationships.template.data.id === template.id &&
              content.attributes.status === 'active',
          )
        : null;
      return matchedContent ? matchedContent : getEmptyContent(resourceName, template);
    });
  };

  const getEmailTemplates = (contents, channel) => {
    Promise.resolve(actions.getEmailTemplates()).then((emailTemplates) => {
      const resourceName = channel.data.attributes.name;

      setPolicyTemplates(emailTemplates.policyEmails);
      setRenewalTemplates(emailTemplates.renewalEmails);

      const policyEmailContent = getContents(
        emailTemplates.policyEmails,
        contents,
        resourceName,
      );
      change('meta.email.policy.content', policyEmailContent);

      const renewalEmailContent = getContents(
        emailTemplates.renewalEmails,
        contents,
        resourceName,
      );
      change('meta.email.renewal.content', renewalEmailContent);
    });
  };

  const getLetterTemplates = (contents, channel) => {
    Promise.resolve(actions.getLetterTemplates()).then((letterTemplates) => {
      const resourceName = channel.data.attributes.name;
      setRenewalLetterTemplates(letterTemplates.renewalLetters);
      const renewalLetterContent = getContents(
        letterTemplates.renewalLetters,
        contents,
        resourceName,
      );
      change('meta.letter.renewal.content', renewalLetterContent);
    });
  };

  const getEmptyContent = (name, template) => {
    const sections = Object.keys(template.attributes.sections).reduce(
      (carry, sectionKey) => {
        carry[sectionKey] = '';
        return carry;
      },
      {},
    );

    return {
      attributes: {
        content_type: 'email',
        name: name + ' ' + template.attributes.name,
        sections,
      },
      relationships: {
        template: {
          data: {
            id: template.id,
          },
        },
      },
    };
  };

  const handleContentFormSubmit = (values) => {
    const checkHasContent = (contents) => {
      return contents.reduce((carry, content) => {
        carry = content !== '' ? true : carry;
        return carry;
      }, false);
    };

    if (values.meta.email) {
      values.meta.email.policy.content = values.meta.email.policy.content.reduce(
        (carry, content) => {
          const values = Object.values(content.attributes.sections);
          if (checkHasContent(values)) {
            carry.push(content);
          }
          return carry;
        },
        [],
      );
      values.meta.email.renewal.content = values.meta.email.renewal.content.reduce(
        (carry, content) => {
          const values = Object.values(content.attributes.sections);
          if (checkHasContent(values)) {
            carry.push(content);
          }
          return carry;
        },
        [],
      );
    }

    if (values.meta.letter) {
      values.meta.letter.renewal.content = values.meta.letter.renewal.content.reduce(
        (carry, content) => {
          const values = Object.values(content.attributes.sections);
          if (checkHasContent(values)) {
            carry.push(content);
          }
          return carry;
        },
        [],
      );
    }

    Promise.resolve(channelActions.createOwnerContent(values)).then(() => {
      handleSetUpOwnerContent();
    });
  };

  if (!formValues) {
    return null;
  }

  return (
    <>
      <Box>
        <h2 className="resource-name">Just Travel Whitelabel Content</h2>

        <Form onSubmit={handleSubmit(handleContentFormSubmit)}>
          <EmailContent
            templates={policyTemplates}
            emails={formValues.meta.email.policy.content}
            emailCategory={'policy'}
          />

          <EmailContent
            templates={renewalTemplates}
            emailCategory={'renewal'}
            emails={formValues.meta.email.renewal.content}
          />

          <LetterContent
            templates={renewalLetterTemplates}
            letterCategory={'renewal'}
            letters={formValues.meta.letter.renewal.content}
          />
          <Row>
            <Col sm={12}>
              <InternalButtonBar
                buttons={[
                  {
                    render: true,
                    permissions: ['content_management.edit'],
                    button: (
                      <Button
                        key={'wl'}
                        className="pull-right"
                        type="submit"
                        bsStyle="primary"
                        isLoading={submitting}
                        disabled={submitting}
                        label={'Save Content'}
                      />
                    ),
                  },
                ]}
              />
            </Col>
          </Row>
        </Form>
      </Box>
    </>
  );
};

const FORM_NAME = 'ownerContentForm';

export default compose(
  connect(
    (state) => {
      return {
        initialValues: {
          meta: {
            email: {
              policy: {
                content: [],
              },
              renewal: {
                content: [],
              },
            },
            letter: {
              renewal: {
                content: [],
              },
            },
          },
        },
        formValues: getFormValues(FORM_NAME)(state),
        isSubmitting: state.broker.isSubmitting,
      };
    },
    (dispatch) => ({
      channelActions: bindActionCreators(channelActions, dispatch),
      actions: bindActionCreators(brokerActions, dispatch),
    }),
  ),
  reduxForm({ form: FORM_NAME }),
)(OwnerContent);
